import React from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import DialogContent from '@material-ui/core/DialogContent';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Icon from '@material-ui/core/Icon';
import ExploreOutlinedIcon from '@material-ui/icons/ExploreOutlined';

// CONSTANTS
import { exitModalSessionStorageKey } from '@/constants/app';

// UTILITY
import { customAnalyticsEvent, analyticsGetUserId } from '@/utility/analytics';

// HOOKS
import useSessionStorage from '@/hooks/useSessionStorage';
import useRouterLock from '@/hooks/useRouterLock';

// ASSETS
import { ReactComponent as FMBExitPopupIconSvg } from '@/assets/images/fmb-exit-popup-icon.svg';

// STYLES
import { DialogStyled, CloseIcon } from './styles';

const DialogExitPopup = ({
  onClose,
  title,
  titleProps,
  icon,
  image,
  subtitle,
  subtitleProps,
  buttonConfirmTitle,
  buttonConfirmProps,
  allowedRoutes,
  confirmRoute,
  analyticsId,
  partnerName,
  ...props
}) => {
  const [open, setOpen] = React.useState(false);
  const [alreadyShown, setAlreadyShown] = useSessionStorage(exitModalSessionStorageKey, false);

  function handleOpen() {
    if (alreadyShown) {
      return;
    }

    setOpen(true);
    setAlreadyShown(true);
    customAnalyticsEvent('exit popup was shown', {
      unique_id: analyticsId || analyticsGetUserId(),
      popup_type: 'marketplace',
      partner: partnerName,
    });
  }

  function handleMouseLeave(e) {
    if (e.clientY < 0) {
      handleOpen();
    }
  }

  const { routeTo, routeToLast } = useRouterLock(allowedRoutes, handleOpen, alreadyShown);

  function handleClose() {
    setOpen(false);
    routeToLast();
  }

  function handleConfirm() {
    setOpen(false);
    routeTo(confirmRoute);
  }

  React.useEffect(() => {
    document.body.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      document.body?.removeEventListener('mouseleave', handleMouseLeave);
    };
  });

  return (
    <DialogStyled fullWidth maxWidth="xs" onClose={handleClose} open={open} {...props}>
      <DialogContent>
        <CloseIcon size="medium" aria-label="close" onClick={handleClose}>
          <i className="icon-close" />
        </CloseIcon>
        {image && <div className="dialog-cta__image-wrapper m-b-15">{image}</div>}
        <div className="dialog-cta__title-wrapper">
          <Typography
            variant="h4"
            align="center"
            {...titleProps}
            className={clsx('dialog-cta__title m-b-15', titleProps.className)}
          >
            {title}
          </Typography>
          {icon && <div className="dialog-cta__icon">{icon}</div>}
        </div>
        <Typography
          align="center"
          {...subtitleProps}
          className={clsx('dialog-cta__subtitle m-b-15', subtitleProps.className)}
        >
          {subtitle}
        </Typography>
        <div className="fmb-logo">
          <Icon onClick={handleConfirm} component={FMBExitPopupIconSvg} />
        </div>
        <Button
          fullWidth
          color="primary"
          onClick={handleConfirm}
          endIcon={<ExploreOutlinedIcon />}
          {...buttonConfirmProps}
          className={clsx('sizeLarge dialog-cta__confirm-btn', buttonConfirmProps.className)}
        >
          {buttonConfirmTitle}
        </Button>
      </DialogContent>
    </DialogStyled>
  );
};

DialogExitPopup.defaultProps = {
  buttonConfirmProps: {},
  titleProps: {},
  subtitleProps: {},
  onClose: () => {},
};

DialogExitPopup.propTypes = {
  onClose: PropTypes.func,
  title: PropTypes.any.isRequired,
  titleProps: PropTypes.object,
  subtitle: PropTypes.any,
  subtitleProps: PropTypes.object,
  icon: PropTypes.any,
  image: PropTypes.any,
  buttonConfirmTitle: PropTypes.string,
  buttonConfirmProps: PropTypes.object,
  allowedRoutes: PropTypes.arrayOf(PropTypes.string),
  confirmRoute: PropTypes.string,
  analyticsId: PropTypes.string,
  partnerName: PropTypes.string,
};

export default React.memo(DialogExitPopup);
